/**
 * Frontend file validation utilities
 * ----------------------------------
 * Supported:
 * - Images: PNG, JPG, JPEG
 * - Documents: PDF
 * - Videos: MP4
 *
 * Blocks:
 * - SVG, HTML, JS, executables
 * - Double extensions
 * - Fake MIME / fake signatures
 * - Embedded HTML / script payloads
 *
 * NOTE:
 * Frontend = early detection only
 * Backend = final security authority
 */

/* ===============================
 Constants
 ===============================*/

const ALLOWED_EXTENSIONS = ['png', 'jpg', 'jpeg', 'pdf', 'mp4'];

const ALLOWED_MIME_TYPES = {
  png: ['image/png'],
  jpg: ['image/jpeg'],
  jpeg: ['image/jpeg'],
  pdf: ['application/pdf'],
  mp4: ['video/mp4'],
};

// Maximum file size: 50MB for videos, 10MB for images/PDFs
const MAX_FILE_SIZE = {
  mp4: 100 * 1024 * 1024, // 100MB
  png: 20 * 1024 * 1024, // 20MB
  jpg: 20 * 1024 * 1024, // 20MB
  jpeg: 20 * 1024 * 1024, // 20MB
  pdf: 20 * 1024 * 1024, // 20MB
};

const DANGEROUS_EXTENSIONS = [
  'html',
  'htm',
  'svg',
  'js',
  'php',
  'asp',
  'aspx',
  'jsp',
  'exe',
  'bat',
  'sh',
  'cmd',
  'vbs',
  'ps1',
  'jar',
  'apk',
  'scr',
  'msi',
];

/* ===============================
0️⃣ Filename Sanitization (for display)
===============================*/
export function sanitizeFilename(filename) {
  if (!filename) return 'unnamed';

  // Remove any HTML tags, quotes, and special characters that could cause XSS
  return filename
    .replace(/[<>'"]/g, '') // Remove HTML-related chars
    .replace(/[^\w\s\-\.]/g, '') // Keep only alphanumeric, spaces, hyphens, dots
    .trim()
    .substring(0, 255); // Limit length
}

/* ===============================
1️⃣ Filename & Extension Security
===============================*/
export function validateFilenameSecurity(filename) {
  if (!filename) {
    return { valid: false, error: 'Filename is required' };
  }

  // Block null bytes and control characters that could be used for bypasses
  if (/[\x00-\x1f\x7f-\x9f]/.test(filename)) {
    return { valid: false, error: 'Invalid characters in filename' };
  }

  // Block path traversal attempts
  if (filename.includes('..') || filename.includes('/') || filename.includes('\\')) {
    return { valid: false, error: 'Invalid filename pattern' };
  }

  // Block files starting with dot (hidden files)
  if (filename.startsWith('.')) {
    return { valid: false, error: 'Hidden files are not allowed' };
  }

  const parts = filename.toLowerCase().split('.');
  if (parts.length > 2) {
    return { valid: false, error: 'File has more than one extension' };
  }
  if (parts.length < 2) {
    return { valid: false, error: 'File has no extension' };
  }

  const extensions = parts.slice(1);
  const lastExt = extensions.at(-1);

  if (extensions.some((ext) => DANGEROUS_EXTENSIONS.includes(ext))) {
    return {
      valid: false,
      error: 'Dangerous file extension detected',
    };
  }

  if (extensions.length > 1) {
    return {
      valid: false,
      error: 'Double extension is not allowed',
    };
  }

  /* Allowlist enforcement */
  if (!ALLOWED_EXTENSIONS.includes(lastExt)) {
    return {
      valid: false,
      error: 'File type not allowed',
    };
  }

  return { valid: true, extension: lastExt };
}

/* ===============================
 2️⃣ MIME Type Validation
===============================*/
function validateMimeType(file, extension) {
  const allowed = ALLOWED_MIME_TYPES[extension] || [];
  return allowed.includes(file.type);
}

/* ===============================
 3️⃣ Magic Bytes Validation
===============================*/
async function validateMagicBytes(file, extension) {
  const buffer = await file.arrayBuffer();
  const bytes = new Uint8Array(buffer);

  if (bytes.length < 12) return false;

  /* JPEG → FF D8 FF */
  if (extension === 'jpg' || extension === 'jpeg') {
    return bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff;
  }

  /* PNG → 89 50 4E 47 0D 0A 1A 0A */
  if (extension === 'png') {
    return (
      bytes[0] === 0x89 &&
      bytes[1] === 0x50 &&
      bytes[2] === 0x4e &&
      bytes[3] === 0x47 &&
      bytes[4] === 0x0d &&
      bytes[5] === 0x0a &&
      bytes[6] === 0x1a &&
      bytes[7] === 0x0a
    );
  }

  /* PDF → %PDF */
  if (extension === 'pdf') {
    return (
      bytes[0] === 0x25 && // %
      bytes[1] === 0x50 && // P
      bytes[2] === 0x44 && // D
      bytes[3] === 0x46 // F
    );
  }

  /* MP4 → "ftyp" at bytes 4-7 */
  if (extension === 'mp4') {
    if (bytes.length < 12) return false;
    // MP4 signature: ftyp at offset 4
    return (
      bytes[4] === 0x66 && // f
      bytes[5] === 0x74 && // t
      bytes[6] === 0x79 && // y
      bytes[7] === 0x70 // p
    );
  }

  return false;
}

/* ===============================
4️⃣ Suspicious Content Heuristic
===============================*/
async function containsDangerousContent(file, extension) {
  const buffer = await file.arrayBuffer();
  // Scan first 50KB and last 10KB (where payloads are often hidden)
  const headerSlice = buffer.slice(0, 51200);
  const footerSlice = buffer.slice(-10240);

  const headerText = new TextDecoder('utf-8', { fatal: false }).decode(headerSlice).toLowerCase();
  const footerText = new TextDecoder('utf-8', { fatal: false }).decode(footerSlice).toLowerCase();
  const combinedText = headerText + ' ' + footerText;

  /* XSS and injection patterns */
  const dangerousPatterns = [
    '<script',
    '</script>',
    '<iframe',
    '</iframe>',
    '<html',
    '</html>',
    '<svg',
    '</svg>',
    '<embed',
    '<object',
    '<!doctype',
    'javascript:',
    'onload=',
    'onerror=',
    'onclick=',
    'onmouseover=',
    'onfocus=',
    'onblur=',
    '<form',
    '<input',
    'eval(',
    'expression(',
    'vbscript:',
    'data:text/html',
    '<link',
    '<style',
    'import(',
  ];

  if (dangerousPatterns.some((p) => combinedText.includes(p))) {
    return true;
  }

  /* Check for PHP tags */
  if (combinedText.includes('<?php') || combinedText.includes('<?=')) {
    return true;
  }

  /* Binary files should NOT contain HTML/script content */
  if (['png', 'jpg', 'jpeg', 'mp4'].includes(extension)) {
    if (combinedText.includes('<html') || combinedText.includes('<script')) {
      return true;
    }
  }

  /* PDF-specific checks - PDFs can contain JavaScript */
  if (extension === 'pdf') {
    if (
      combinedText.includes('/js') ||
      combinedText.includes('/javascript') ||
      combinedText.includes('/launch') ||
      combinedText.includes('/openaction')
    ) {
      return true;
    }
  }

  return false;
}

/* ===============================
5️⃣ Unified Validator
===============================*/
export async function validateSuspiciousFile(file) {
  if (file.name.toLowerCase().split('.').length > 2) {
    return { valid: false, error: 'File type not supported' };
  }

  if (
    file.name.toLowerCase().endsWith('.csv') ||
    file.name.toLowerCase().endsWith('.xls') ||
    file.name.toLowerCase().endsWith('.xlsx')
  ) {
    return { valid: true };
  }

  // Validate file object exists
  if (!file || !file.name || file.size === undefined) {
    return { valid: false, error: 'Invalid file object' };
  }

  /* Block SVG explicitly (XSS vector) */
  if (file?.type === 'image/svg+xml' || file.name.toLowerCase().endsWith('.svg')) {
    return { valid: false, error: 'SVG files are not allowed due to security risks' };
  }

  /* 1️⃣ Filename security */
  const nameCheck = validateFilenameSecurity(file.name);
  if (!nameCheck.valid) {
    return nameCheck;
  }

  const { extension } = nameCheck;

  /* 2️⃣ File size validation */
  const maxSize = MAX_FILE_SIZE[extension];
  if (!maxSize) {
    return { valid: false, error: 'File type not supported' };
  }
  if (file.size > maxSize) {
    const maxSizeMB = Math.floor(maxSize / (1024 * 1024));
    return {
      valid: false,
      error: `File too large. Maximum size for ${extension.toUpperCase()} is ${maxSizeMB}MB`,
    };
  }

  /* Block empty files */
  if (file.size === 0) {
    return { valid: false, error: 'Empty files are not allowed' };
  }

  /* 3️⃣ MIME type validation */
  if (!validateMimeType(file, extension)) {
    return { valid: false, error: 'File type does not match extension' };
  }

  /* 4️⃣ Magic bytes (file signature) validation */
  if (!(await validateMagicBytes(file, extension))) {
    return { valid: false, error: 'Invalid file format or corrupted file' };
  }

  /* 5️⃣ Heuristic content scan for XSS/injection payloads */
  if (await containsDangerousContent(file, extension)) {
    return { valid: false, error: 'File contains suspicious or malicious content' };
  }

  return { valid: true };
}
